import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid, Typography } from '@mui/material';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

import {
  OPERATING_EXPENSES_NAMES,
  operatingExpensesNameArray,
  useGetOperatingExpensesTableHeadings,
} from './utils/form';
import { FIRST_COLUMN_WIDTH, operatingExpensesPrefix } from '../../../utils/form';
import { getEachRowValues } from '../NetSalesForm/helpers';

import styles from './OperatingExpensesForm.module.scss';

export const OperatingExpensesForm: FC<{ disabled: boolean }> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { isExamplePage} = useExamplePreview()

  const { getValues } = useFormContext();
  const { operatingExpensesLinkedFields, totalRowTitle } = useGetOperatingExpensesTableHeadings(isExamplePage);

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const totalExpensesRowValues = getValues([
    concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.R_AND_D),
    concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.SALES_AND_MARKETING),
    concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.GENERAL_AND_ADMINISTRATIVE),
    concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.OTHER_OPERATING_EXPENSES),
  ]);
  const totalOperatingExpenses = getValues(
    concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.TOTAL_OPERATING_EXPENSES),
  );

  return (
    <Grid container direction={'column'}>
      <Grid item xs>
        <Typography className={styles.sticky} variant="subtitle2">
          {t('financialPlan.profitAndLossProjection.operatingExpenses.title')}
        </Typography>
      </Grid>
      {totalExpensesRowValues.map((_rowValue: Record<string, string>, index: number) => {
        const fieldName = concatWithDot(operatingExpensesPrefix, operatingExpensesNameArray[index]);
        return (
          <Grid item xs key={fieldName}>
            <ReadOnlyTableRow
              name={fieldName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              columnValues={getEachRowValues(totalExpensesRowValues, index, period)}
              rowTitle={operatingExpensesLinkedFields[index]}
              noNeedName={true}
              disabled={disabled}
            />
          </Grid>
        );
      })}
      <Grid item xs>
        <TotalRowComponent
          name={concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.TOTAL_OPERATING_EXPENSES)}
          columnValues={getEachRowValues([totalOperatingExpenses], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          totalRowTitle={totalRowTitle}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};
